import { Box } from '@material-ui/core';
import { Agent } from '@nemuru-eng/js-api-client';

import { FormGridContainer } from '@/components/shared/components/FormGridContainer/FormGridContainer';
import { FormGridOptions } from '@/components/shared/components/FormGridContainer/types/formGridOptions';
import { AutocompleteSelectFormField } from '@/components/shared/components/NewContextForms/AutocompleteSelectFormField';
import { MultiSelectFormField } from '@/components/shared/components/NewContextForms/MultiSelectFormField';
import { NumberFormField } from '@/components/shared/components/NewContextForms/NumberFormField';
import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { useTranslation } from '@/components/shared/hooks/useTranslation';

import { ErrorSuccessStatusIndicator } from '../../ErrorSuccessStatusIndicator';
import { AllowedArticleTypesOptions } from '../constants/AllowedArticleTypesOptions';
import { AllowedAssetTypesOptions } from '../constants/AllowedAssetTypesOptions';
import { AllowedOnboardingFulfilledByOptions } from '../constants/AllowedOnboardingFulfilledByOptions';
import { AllowedPaymentMethodTypesOptions } from '../constants/AllowedPaymentMethodsOptions';
import { AllowedProductsOptions } from '../constants/AllowedProductsOptions';
import { AllowedPurchaseCountriesOptions } from '../constants/AllowedPurchaseCountriesOptions';
import { DEFAULT_ENABLED_DISABLED_VALUES } from '../constants/constants';
import { CustomLanguageOptions } from '../constants/CustomLanguageOptions';
import { CustomRegistryOptions } from '../constants/CustomRegistryOptions';
import { CustomThemeOptions } from '../constants/CustomThemeOptions';

interface ConfigurationFormProps extends FormGridOptions {
  currentAgent: Agent;
}

export const ConfigurationForm = ({ currentAgent, fullWidth = false, fieldVariant }: ConfigurationFormProps) => {
  const { getTextIdTranslation } = useTranslation();
  const EnabledValueIndicator = (value: boolean) => (
    <>
      {value ? getTextIdTranslation('ENABLED') : getTextIdTranslation('DISABLED')}
      <ErrorSuccessStatusIndicator value={value} />
    </>
  );

  return (
    <Box pt={2}>
      <FormGridContainer fieldVariant={fieldVariant} fullWidth={fullWidth} numberOfFieldsInFirstColumn={11}>
        <TextFormField value={currentAgent.name} name={'name'} label={'AGENT_NAME'} />
        <AutocompleteSelectFormField
          name={'customTheme'}
          label={'CUSTOM_THEME'}
          value={currentAgent.customTheme}
          selectOptions={CustomThemeOptions(getTextIdTranslation)}
        />
        <AutocompleteSelectFormField
          name={'customRegistry'}
          label={'CUSTOM_REGISTRY'}
          value={currentAgent.customRegistry}
          selectOptions={CustomRegistryOptions(getTextIdTranslation)}
        />
        <SelectFormField
          name={'customLanguage'}
          label={'CUSTOM_LANGUAGE'}
          value={currentAgent.customLanguage}
          selectOptions={CustomLanguageOptions}
        />
        <SelectFormField
          name={'communicationsAreAllowed'}
          label={'COMMUNICATIONS'}
          value={EnabledValueIndicator(currentAgent.communicationsAreAllowed)}
          selectOptions={DEFAULT_ENABLED_DISABLED_VALUES}
        />
        <SelectFormField
          name={'isBankReaderEnabled'}
          label={'BANK_READER_ENABLED'}
          value={EnabledValueIndicator(currentAgent.isBankReaderEnabled)}
          selectOptions={DEFAULT_ENABLED_DISABLED_VALUES}
        />
        <SelectFormField
          name={'quotaSelectionByBorrowerIsAllowed'}
          label={'QUOTA_SELECTION_ALLOWED'}
          value={EnabledValueIndicator(currentAgent.quotaSelectionByBorrowerIsAllowed)}
          selectOptions={DEFAULT_ENABLED_DISABLED_VALUES}
        />
        <SelectFormField
          name={'isDocumentationRequiredManagementEnabled'}
          label={'DOCUMENT_REQUIRED_MANAGEMENT'}
          value={EnabledValueIndicator(currentAgent.isDocumentationRequiredManagementEnabled)}
          selectOptions={DEFAULT_ENABLED_DISABLED_VALUES}
        />
        <SelectFormField
          name={'areCertificationsEnabled'}
          label={'CERTIFICATIONS'}
          value={EnabledValueIndicator(currentAgent.areCertificationsEnabled)}
          selectOptions={DEFAULT_ENABLED_DISABLED_VALUES}
        />
        <SelectFormField
          name={'isStatusAnalysingEnabled'}
          label={'IS_STATUS_ANALYSING_ENABLED'}
          value={EnabledValueIndicator(currentAgent.isStatusAnalysingEnabled)}
          selectOptions={DEFAULT_ENABLED_DISABLED_VALUES}
        />
        <NumberFormField
          value={currentAgent.maxBankReaderAttempts}
          name={'maxBankReaderAttempts'}
          label={'BANK_READER_MAX_ATTEMPTS'}
          type="number"
          allowDecimals={false}
        />

        <MultiSelectFormField
          value={currentAgent.allowedPurchaseCountries}
          label={'ALLOWED_PURCHASE_COUNTRIES'}
          name={'allowedPurchaseCountries'}
          selectOptions={AllowedPurchaseCountriesOptions}
        />
        <MultiSelectFormField
          value={currentAgent.allowedAssetTypes}
          label={'ALLOWED_ASSET_TYPES'}
          name={'allowedAssetTypes'}
          selectOptions={AllowedAssetTypesOptions}
        />
        {fieldVariant === FieldVariant.dataRow && (
          <MultiSelectFormField
            value={currentAgent.allowedProducts}
            label={'ALLOWED_PRODUCTS'}
            name={'allowedProducts'}
            selectOptions={AllowedProductsOptions}
            disabled
          />
        )}
        <MultiSelectFormField
          value={currentAgent.allowedOnboardingFulfilledByTypes}
          label={'ALLOWED_ONBOARDING_FULFILLED_BY_TYPES'}
          name={'allowedOnboardingFulfilledByTypes'}
          selectOptions={AllowedOnboardingFulfilledByOptions}
        />
        <MultiSelectFormField
          value={currentAgent.allowedArticleTypes}
          label={'ALLOWED_ARTICLE_TYPES'}
          name={'allowedArticleTypes'}
          selectOptions={AllowedArticleTypesOptions}
        />
        <MultiSelectFormField
          value={currentAgent.allowedPaymentMethodTypes}
          label={'ALLOWED_PAYMENT_METHODS'}
          name={'allowedPaymentMethodTypes'}
          selectOptions={AllowedPaymentMethodTypesOptions}
        />
      </FormGridContainer>
    </Box>
  );
};
